import React, { useState } from "react";
import { Edit, Trash2 } from "lucide-react";

const rolesAPI = [
  { value: "principal", label: "Principal", color: "bg-blue-100 text-blue-800" },
  { value: "gestion", label: "Gestión", color: "bg-indigo-100 text-indigo-800" },
  { value: "consulta", label: "Consulta", color: "bg-gray-100 text-gray-800" },
];

const estadosAPI = [
  { value: "Activo", color: "bg-green-100 text-green-800" },
  { value: "Pendiente", color: "bg-yellow-100 text-yellow-800" },
  { value: "Bloqueado", color: "bg-red-100 text-red-800" },
  { value: "Inactivo", color: "bg-gray-200 text-gray-700" },
];

const usuariosIniciales = [
  {
    id: 1,
    correo: "admin@empresa.com",
    nombres: "Carlos",
    apellidos: "Pérez",
    telefono: "3001234567",
    rol: "principal",
    estado: "Activo",
  },
  {
    id: 2,
    correo: "gestion@empresa.com",
    nombres: "María",
    apellidos: "Gómez",
    telefono: "3109876543",
    rol: "gestion",
    estado: "Pendiente",
  },
  {
    id: 3,
    correo: "consulta@empresa.com",
    nombres: "Luis",
    apellidos: "Morales",
    telefono: "3104561234",
    rol: "consulta",
    estado: "Inactivo",
  },
  {
    id: 4,
    correo: "soporte@empresa.com",
    nombres: "Andrea",
    apellidos: "Díaz",
    telefono: "3110001000",
    rol: "gestion",
    estado: "Bloqueado",
  },
];

export default function UsuariosCuentasPage() {
  const [usuarios, setUsuarios] = useState(usuariosIniciales);
  const [modal, setModal] = useState(false);
  const [editando, setEditando] = useState(false);
  const [usuarioEdit, setUsuarioEdit] = useState(null);
  const [form, setForm] = useState({
    correo: "",
    contrasena: "",
    confirmarContrasena: "",
    nombres: "",
    apellidos: "",
    telefono: "",
    rol: "gestion",
    estado: "Pendiente",
  });

  const empresa = { nit: "900999999", razonSocial: "Tiendas Fedelza S.A.S" };

  const passwordValida = (pwd) =>
    pwd.length >= 8 &&
    /[A-Z]/.test(pwd) &&
    /[a-z]/.test(pwd) &&
    /[0-9]/.test(pwd) &&
    /[^A-Za-z0-9]/.test(pwd);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const abrirModal = (user = null) => {
    setEditando(!!user);
    setUsuarioEdit(user);
    if (user) {
      setForm({
        correo: user.correo,
        contrasena: "",
        confirmarContrasena: "",
        nombres: user.nombres,
        apellidos: user.apellidos,
        telefono: user.telefono,
        rol: user.rol,
        estado: user.estado,
      });
    } else {
      setForm({
        correo: "",
        contrasena: "",
        confirmarContrasena: "",
        nombres: "",
        apellidos: "",
        telefono: "",
        rol: "gestion",
        estado: "Pendiente",
      });
    }
    setModal(true);
  };

  const cerrarModal = () => {
    setModal(false);
    setUsuarioEdit(null);
    setForm({
      correo: "",
      contrasena: "",
      confirmarContrasena: "",
      nombres: "",
      apellidos: "",
      telefono: "",
      rol: "gestion",
      estado: "Pendiente",
    });
  };

  const guardarUsuario = (e) => {
    e.preventDefault();
    if (!form.correo.match(/^[\w-.]+@[\w-]+(\.[\w-]+)+$/)) {
      alert("Correo electrónico inválido.");
      return;
    }
    if (!editando && (!form.contrasena || !passwordValida(form.contrasena))) {
      alert(
        "La contraseña debe tener mínimo 8 caracteres, una mayúscula, una minúscula, un número y un símbolo."
      );
      return;
    }
    if (!editando && form.contrasena !== form.confirmarContrasena) {
      alert("Las contraseñas no coinciden.");
      return;
    }
    if (!form.nombres.trim() || !form.apellidos.trim() || !form.telefono.trim()) {
      alert("Nombres, apellidos y teléfono son obligatorios.");
      return;
    }
    if (editando) {
      setUsuarios((us) =>
        us.map((u) =>
          u.id === usuarioEdit.id
            ? { ...u, ...form }
            : u
        )
      );
    } else {
      if (usuarios.some((u) => u.correo === form.correo)) {
        alert("Ese correo ya existe.");
        return;
      }
      setUsuarios((us) => [
        ...us,
        {
          id: Math.max(...us.map((u) => u.id), 0) + 1,
          ...form,
        },
      ]);
      alert(
        "Usuario creado. El usuario recibirá un correo para activar su cuenta."
      );
    }
    cerrarModal();
  };

  const eliminarUsuario = (id) => {
    if (
      window.confirm(
        "¿Está seguro que desea eliminar este usuario? Esta acción no se puede deshacer."
      )
    ) {
      setUsuarios((us) => us.filter((u) => u.id !== id));
    }
  };

  const getRolBadge = (rol) => {
    const rolInfo = rolesAPI.find((r) => r.value === rol);
    if (!rolInfo) return null;
    return (
      <span
        className={
          "px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap " +
          rolInfo.color
        }
      >
        {rolInfo.label}
      </span>
    );
  };

  const getEstadoBadge = (estado) => {
    const estadoInfo = estadosAPI.find((e) => e.value === estado);
    if (!estadoInfo) return null;
    return (
      <span
        className={
          "px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap " +
          estadoInfo.color
        }
      >
        {estado}
      </span>
    );
  };

  return (
    <div className="bg-white w-full mx-auto rounded-2xl shadow-xl px-4 py-8 border">
      <div className="flex justify-end mb-4">
        <button
          onClick={() => abrirModal()}
          className="bg-[#003865] text-white px-6 py-2 rounded-lg shadow font-bold text-base hover:bg-blue-700 transition"
        >
          + Agregar Usuario
        </button>
      </div>
      {/* Tabla de usuarios, scroll vertical si hay muchos */}
      <div className="overflow-x-auto max-h-[420px]">
        <table className="min-w-full divide-y divide-gray-100 text-xs">
          <thead>
            <tr className="bg-gray-100">
              <th className="px-2 py-1 text-left font-semibold w-[22%]">Nombre</th>
              <th className="px-2 py-1 text-left font-semibold w-[18%]">Correo</th>
              <th className="px-2 py-1 text-left font-semibold w-[10%]">Teléfono</th>
              <th className="px-2 py-1 text-center font-semibold w-[8%]">Rol</th>
              <th className="px-2 py-1 text-center font-semibold w-[8%]">Estado</th>
              <th className="px-2 py-1 text-center font-semibold w-[6%]">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {usuarios.map((u) => (
              <tr key={u.id} className="border-b hover:bg-blue-50">
                <td className="px-2 py-1">{u.nombres} {u.apellidos}</td>
                <td className="px-2 py-1">{u.correo}</td>
                <td className="px-2 py-1">{u.telefono}</td>
                <td className="px-2 py-1 text-center">
                  {getRolBadge(u.rol)}
                </td>
                <td className="px-2 py-1 text-center">
                  {getEstadoBadge(u.estado)}
                </td>
                <td className="px-2 py-1 flex gap-2 justify-center">
                  <button
                    className="hover:bg-blue-100 p-1 rounded transition-transform hover:scale-110"
                    title="Editar"
                    onClick={() => abrirModal(u)}
                  >
                    <Edit className="w-4 h-4 text-blue-700" />
                  </button>
                  {u.rol !== "principal" && (
                    <button
                      className="hover:bg-red-100 p-1 rounded transition-transform hover:scale-110"
                      title="Eliminar"
                      onClick={() => eliminarUsuario(u.id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal responsivo en una sola columna, con scroll */}
      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div
            className="bg-white rounded-xl shadow-lg w-full max-w-xl mx-4 relative flex flex-col"
            style={{
              maxHeight: "90vh",
            }}
          >
            <button
              onClick={cerrarModal}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-xl"
            >
              ×
            </button>
            <div className="overflow-y-auto p-6">
              <h4 className="text-xl font-bold text-[#003865] mb-4">
                {editando ? "Editar usuario" : "Agregar usuario"}
              </h4>
              <form className="space-y-4" onSubmit={guardarUsuario}>
                {/* Correo */}
                <div>
                  <label className="block font-semibold mb-1 text-gray-800">
                    Correo electrónico <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="correo"
                    type="email"
                    value={form.correo}
                    onChange={handleFormChange}
                    className="border border-gray-200 rounded-lg h-10 p-2 w-full"
                    required
                    disabled={editando}
                  />
                </div>
                {/* Contraseña solo al crear */}
                {!editando && (
                  <>
                    <div>
                      <label className="block font-semibold mb-1 text-gray-800">
                        Contraseña <span className="text-red-500">*</span>
                      </label>
                      <input
                        name="contrasena"
                        type="password"
                        value={form.contrasena}
                        onChange={handleFormChange}
                        className="border border-gray-200 rounded-lg h-10 p-2 w-full"
                        required
                        placeholder="Mínimo 8 caracteres, una mayúscula, minúscula, número y símbolo"
                      />
                    </div>
                    <div>
                      <label className="block font-semibold mb-1 text-gray-800">
                        Confirmar contraseña <span className="text-red-500">*</span>
                      </label>
                      <input
                        name="confirmarContrasena"
                        type="password"
                        value={form.confirmarContrasena}
                        onChange={handleFormChange}
                        className="border border-gray-200 rounded-lg h-10 p-2 w-full"
                        required
                      />
                    </div>
                  </>
                )}
                {/* Nombres */}
                <div>
                  <label className="block font-semibold mb-1 text-gray-800">
                    Nombres <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="nombres"
                    type="text"
                    value={form.nombres}
                    onChange={handleFormChange}
                    className="border border-gray-200 rounded-lg h-10 p-2 w-full"
                    required
                  />
                </div>
                {/* Apellidos */}
                <div>
                  <label className="block font-semibold mb-1 text-gray-800">
                    Apellidos <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="apellidos"
                    type="text"
                    value={form.apellidos}
                    onChange={handleFormChange}
                    className="border border-gray-200 rounded-lg h-10 p-2 w-full"
                    required
                  />
                </div>
                {/* Teléfono */}
                <div>
                  <label className="block font-semibold mb-1 text-gray-800">
                    Teléfono <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="telefono"
                    type="text"
                    value={form.telefono}
                    onChange={handleFormChange}
                    className="border border-gray-200 rounded-lg h-10 p-2 w-full"
                    required
                    maxLength={10}
                  />
                </div>
                {/* Rol */}
                <div>
                  <label className="block font-semibold mb-1 text-gray-800">
                    Rol/Perfil <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="rol"
                    value={form.rol}
                    onChange={handleFormChange}
                    className="border border-gray-200 rounded-lg h-10 p-2 w-full"
                    required
                  >
                    {rolesAPI.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </div>
                {/* Empresa asociada (solo visual) */}
                <div>
                  <label className="block font-semibold mb-1 text-gray-800">
                    Empresa asociada
                  </label>
                  <div className="border border-gray-200 rounded-lg h-10 p-2 w-full bg-gray-50 text-gray-600">
                    {empresa.nit} - {empresa.razonSocial}
                  </div>
                </div>
                <div className="flex justify-end pt-2">
                  <button
                    type="submit"
                    className="bg-[#003865] text-white px-8 py-2 rounded-xl shadow font-bold text-base hover:bg-blue-700 transition"
                  >
                    {editando ? "Guardar cambios" : "Crear usuario"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
